import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { apiPostForm } from "@/lib/api";
import type { BizPost } from "@/lib/types";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";

const MAX_LEN = 3000;

export default function PromoWritePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { member, requireLogin } = useAuth();

  const editNo = searchParams.get("no");
  const isEdit = Boolean(editNo);

  const { data: existing, loading: loadingExisting, error: loadError, reload } =
    useApi<BizPost>(editNo ? `bizposts.php?no=${editNo}` : null);

  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [showTel, setShowTel] = useState(true);
  const [files, setFiles] = useState<(File | null)[]>([null, null, null]);
  const [submitting, setSubmitting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);
  const fileRefs = [ref1, ref2, ref3];

  // 수정 모드: 기존 글 불러와 채우기
  useEffect(() => {
    if (isEdit && existing && !prefilled) {
      setSubject(existing.subject ?? "");
      setContent(existing.content ?? "");
      setShowTel((existing.show_tel ?? 1) !== 0);
      setPrefilled(true);
    }
  }, [isEdit, existing, prefilled]);

  useEffect(() => {
    if (!member) requireLogin();
  }, [member, requireLogin]);

  if (!member) {
    return (
      <>
        <PageBanner
          title="직업홍보 글쓰기"
          breadcrumb={[{ label: "직업홍보", to: "/promo" }, { label: "글쓰기" }]}
        />
        <div className="mx-auto max-w-2xl px-4 md:px-6 py-16 text-center text-foreground-500">
          로그인 후 글을 작성할 수 있습니다.
        </div>
      </>
    );
  }

  const setFile = (index: number, file: File | null) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? file : f)));
  };

  const submit = async () => {
    const s = subject.trim();
    const c = content.trim();
    if (!s) {
      toast("제목을 입력해 주세요.", "info");
      return;
    }
    if (!c) {
      toast("내용을 입력해 주세요.", "info");
      return;
    }
    if (c.length > MAX_LEN) {
      toast(`내용은 ${MAX_LEN}자 이내로 입력해 주세요.`, "info");
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("act", isEdit ? "modify" : "write");
      if (isEdit && editNo) form.append("no", editNo);
      form.append("subject", s);
      form.append("content", c);
      form.append("show_tel", showTel ? "1" : "0");
      files.forEach((f, i) => {
        if (f) form.append(`img${i + 1}`, f);
      });
      await apiPostForm("bizpost_write.php", form);
      toast(isEdit ? "글이 수정되었습니다." : "글이 등록되었습니다.", "success");
      navigate("/promo");
    } catch (e) {
      toast(e instanceof Error ? e.message : "저장에 실패했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && loadingExisting) {
    return (
      <>
        <PageBanner
          title="직업홍보 수정"
          breadcrumb={[{ label: "직업홍보", to: "/promo" }, { label: "수정" }]}
        />
        <section className="mx-auto max-w-2xl px-4 md:px-6 py-10 space-y-5">
          <Skeleton className="h-12 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </section>
      </>
    );
  }

  if (isEdit && loadError) {
    return (
      <>
        <PageBanner
          title="직업홍보 수정"
          breadcrumb={[{ label: "직업홍보", to: "/promo" }, { label: "수정" }]}
        />
        <section className="mx-auto max-w-2xl px-4 md:px-6 py-12">
          <EmptyState
            icon="ri-error-warning-line"
            title="글을 불러오지 못했습니다"
            description={loadError}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner
        title={isEdit ? "직업홍보 수정" : "직업홍보 글쓰기"}
        description="회원 사업장의 소식과 홍보 내용을 남겨보세요."
        breadcrumb={[
          { label: "직업홍보", to: "/promo" },
          { label: isEdit ? "수정" : "글쓰기" },
        ]}
      />

      <section className="mx-auto max-w-2xl px-4 md:px-6 py-10 md:py-12">
        <div className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-7 space-y-5">
          <div>
            <label htmlFor="subject" className="mb-2 block text-sm font-semibold text-foreground-700">
              제목
            </label>
            <input
              id="subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={100}
              placeholder="홍보 글 제목을 입력하세요"
              className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div>
            <label htmlFor="content" className="mb-2 block text-sm font-semibold text-foreground-700">
              내용
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={MAX_LEN}
              rows={10}
              placeholder="사업장 소개와 홍보 내용을 입력하세요"
              className="w-full resize-y rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <p className="mt-1.5 text-right text-xs text-foreground-400">
              {content.length} / {MAX_LEN}
            </p>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-foreground-700">사진 첨부 (최대 3장)</p>
            <div className="flex flex-wrap gap-3">
              {[0, 1, 2].map((i) => {
                const file = files[i];
                return (
                  <div key={i} className="relative">
                    <button
                      type="button"
                      onClick={() => fileRefs[i].current?.click()}
                      className="h-24 w-24 overflow-hidden rounded-lg border border-dashed border-background-300 bg-background-50 text-foreground-400 hover:border-primary-400 hover:text-primary-600"
                    >
                      {file ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`첨부 이미지 ${i + 1}`}
                          className="h-full w-full object-cover object-top"
                        />
                      ) : (
                        <span className="flex h-full w-full flex-col items-center justify-center gap-1">
                          <i className="ri-image-add-line text-2xl" aria-hidden="true"></i>
                          <span className="text-xs">사진 {i + 1}</span>
                        </span>
                      )}
                    </button>
                    {file && (
                      <button
                        type="button"
                        onClick={() => setFile(i, null)}
                        aria-label="사진 삭제"
                        className="absolute -right-2 -top-2 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-900 text-background-50"
                      >
                        <i className="ri-close-line text-sm" aria-hidden="true"></i>
                      </button>
                    )}
                    <input
                      ref={fileRefs[i]}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        if (f && f.type.startsWith("image/")) setFile(i, f);
                        e.target.value = "";
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4 cursor-pointer">
            <input
              type="checkbox"
              checked={showTel}
              onChange={(e) => setShowTel(e.target.checked)}
              className="h-4 w-4 accent-primary-500"
            />
            <span className="text-sm text-foreground-700">
              문의 전화번호를 글에 표시합니다
            </span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="flex-1 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
            >
              {submitting ? "저장 중..." : isEdit ? "수정 완료" : "등록하기"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/promo")}
              className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              취소
            </button>
          </div>
        </div>
      </section>
    </>
  );
}